import React, { useContext } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { YearInput } from 'semantic-ui-calendar-react'
import { Form, Grid, Header, Icon, Segment } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../../..'
import dayjs from 'dayjs'

interface Props {
  index: number
  key: string
  removeCarbon: (index: number) => void
  isLastRow: boolean
  maxYear: number
}

const CropChange: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { index, key, removeCarbon, isLastRow, maxYear } = props
  const { control, setValue } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)

  const watchLandFrom = useWatch({ control, name: `carbonChanges[${index}].landUseFrom` })
  const watchTillageFrom = useWatch({ control, name: `carbonChanges[${index}].tillageTypeFrom` })
  const watchCarbonFrom = useWatch({ control, name: `carbonChanges[${index}].carbonInputFrom` })

  const tillageOptions = options?.tillageTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const landUseOptions = options?.landUseTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const carbonInputOptions = options?.carbonInputs?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const landToEnabled = index === 0 && isLastRow && Boolean(watchLandFrom)
  const tillageToEnabled = index === 0 && isLastRow && Boolean(watchTillageFrom)
  const carbonInputToEnabled = index === 0 && isLastRow && Boolean(watchCarbonFrom)
  const minYear = dayjs().year() - 20

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  return <div key={key}>
    <Segment attached>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
        <Header as='h4'>{t('Prior change')} {index + 1}</Header>
        {isLastRow && <Icon
          name='trash'
          color='red'
          size='large'
          style={{ cursor: 'pointer' }}
          onClick={() => removeCarbon(index)}
        />}
      </div>
      <Grid columns={2}>
        <Grid.Row>
          <Grid.Column>
            <Controller
              name={`carbonChanges[${index}].year`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return <Form.Field>
                  <label>{t('Year of Change')}</label>
                  <YearInput
                    name='date'
                    autoComplete='off'
                    placeholder={t('Year')}
                    closable
                    clearIcon={'close'}
                    initialDate={maxYear?.toString() || '2025'}
                    onKeyDown={handleKeyPress}
                    minDate={minYear.toString()}
                    maxDate={maxYear?.toString() || '2025'}
                    clearable
                    closeOnMouseLeave={false}
                    className={'datePicker'}
                    value={field.value}
                    iconPosition='left'
                    onChange={(event, { name, value }) => setValue(`carbonChanges[${index}].year`, value)}
                  />
                </Form.Field>
              }}
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Input
              control={control}
              name={`carbonChanges[${index}].percentage`}
              withoutAsterisk
              label={t('Allocation')}
              placeholder={t('Percentage')}
              rules={{ required: true }}
              suffix=' %'
              typeInput='controled'
              disabled={!isLastRow}
              allowDecimals
              decimalScale={2}
              noErrorMessage
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Land use (from)')}
              name={`carbonChanges[${index}].landUseFrom`}
              rules={{ required: true }}
              withoutAsterisk
              options={landUseOptions}
              disabled={!isLastRow}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Land use (to)')}
              name={`carbonChanges[${index}].landUseTo`}
              withoutAsterisk
              options={landUseOptions}
              disabled={!landToEnabled}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Tillage (from)')}
              name={`carbonChanges[${index}].tillageTypeFrom`}
              rules={{ required: true }}
              withoutAsterisk
              options={tillageOptions}
              disabled={!isLastRow}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Tillage (to)')}
              name={`carbonChanges[${index}].tillageTypeTo`}
              withoutAsterisk
              options={tillageOptions}
              disabled={!tillageToEnabled}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Carbon Inputs (from)')}
              name={`carbonChanges[${index}].carbonInputFrom`}
              withoutAsterisk
              options={carbonInputOptions}
              rules={{ required: true }}
              placeholder={t('Select')}
              disabled={!isLastRow}
              noErrorMessage
            />
          </Grid.Column>
          <Grid.Column>
            <FormFields.Select
              control={control}
              label={t('Carbon Inputs (to)')}
              name={`carbonChanges[${index}].carbonInputTo`}
              withoutAsterisk
              options={carbonInputOptions}
              disabled={!carbonInputToEnabled}
              rules={{ required: true }}
              placeholder={t('Select')}
              noErrorMessage
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  </div>
}
export default CropChange